/**
 * Living Report™ — Sprint 1 Phase 2 (Experience Engine™ Flow A, canon v1.0.0).
 *
 * The report as a journey, not a dashboard: Letter → Your Story™ →
 * Career DNA™ → Your Strengths™ → Growth Opportunities™ → Career Matches™ →
 * One Next Step™. One idea per screen, progressive disclosure, exactly one
 * primary action at the end (V-EXP-03), every score arriving after its
 * story (V-EXP-05).
 *
 * PRESENTATION ONLY. Every sentence about the learner is either
 * (a) canonical template copy — fixed, versioned content, never generated —
 * or (b) quoted verbatim from the sealed Experience Engine payload
 * (explainability.explanation / recommendation explanation). Nothing is
 * computed, inferred, or invented client-side.
 */
import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import OnetAttribution from "@/components/OnetAttribution";
import {
  type StudentPayload, type IntelRow, type FeatureRow, type RecRow,
  pct, bar,
} from "./reportTypes";

/* ------------------------------------------------------------------ */
/* Canonical template copy (versioned content — P-EXP-03 discipline). */
/* ------------------------------------------------------------------ */

const CHAPTERS = [
  "A letter for you",
  "Your Story™",
  "Career DNA™",
  "Your Strengths™",
  "Your Growth Opportunities™",
  "Future Career Matches™",
  "One Next Step™",
] as const;

function firstName(payload: StudentPayload): string | null {
  const n = payload.learner.display_name?.trim();
  if (!n) return null;
  return n.split(/\s+/)[0];
}

/* ------------------------------------------------------------------ */

export default function LivingReport({ payload }: { payload: StudentPayload }) {
  const navigate = useNavigate();
  const [chapter, setChapter] = useState(0);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const mounted = useRef(false);
  // Printing shows every chapter, not just the one on screen.
  const [printAll, setPrintAll] = useState(false);
  useEffect(() => {
    const on = () => setPrintAll(true);
    const off = () => setPrintAll(false);
    window.addEventListener("beforeprint", on);
    window.addEventListener("afterprint", off);
    const mq = window.matchMedia("print");
    const onMq = (e: MediaQueryListEvent) => setPrintAll(e.matches);
    mq.addEventListener?.("change", onMq);
    return () => {
      window.removeEventListener("beforeprint", on);
      window.removeEventListener("afterprint", off);
      mq.removeEventListener?.("change", onMq);
    };
  }, []);

  // Each chapter is one idea; arriving on it announces its heading.
  useEffect(() => {
    if (!mounted.current) { mounted.current = true; return; }
    headingRef.current?.focus();
    window.scrollTo({ top: 0 });
  }, [chapter]);

  const name = firstName(payload);
  const next = () => setChapter((c) => Math.min(c + 1, CHAPTERS.length - 1));
  const back = () => setChapter((c) => Math.max(c - 1, 0));

  return (
    <main className="container mx-auto max-w-2xl px-6 py-12 sm:py-20">
      {/* Chapter position — quiet, non-competing */}
      <nav aria-label="Report chapters" className="mb-10 flex print:hidden items-center justify-center gap-2">
        {CHAPTERS.map((title, i) => (
          <button
            key={title}
            aria-label={`${title}${i === chapter ? " (current)" : ""}`}
            aria-current={i === chapter ? "step" : undefined}
            onClick={() => setChapter(i)}
            className={`h-1.5 rounded-full transition-all duration-500 ${
              i === chapter ? "w-6 bg-primary" : i < chapter ? "w-1.5 bg-primary/50" : "w-1.5 bg-border"
            }`}
          />
        ))}
      </nav>

      {printAll ? (
        <div className="space-y-12">
          <LetterChapter name={name} headingRef={headingRef} onNext={next} />
          <StoryChapter payload={payload} headingRef={headingRef} onNext={next} />
          <DnaChapter payload={payload} headingRef={headingRef} onNext={next} />
          <StrengthsChapter payload={payload} headingRef={headingRef} onNext={next} />
          <GrowthChapter payload={payload} headingRef={headingRef} onNext={next} />
          <MatchesChapter payload={payload} headingRef={headingRef} onNext={next} />
          <NextStepChapter payload={payload} headingRef={headingRef} onAction={() => navigate("/command-center")} />
        </div>
      ) : (
      <div key={chapter} className="animate-fade-in">
        {chapter === 0 && <LetterChapter name={name} headingRef={headingRef} onNext={next} />}
        {chapter === 1 && <StoryChapter payload={payload} headingRef={headingRef} onNext={next} />}
        {chapter === 2 && <DnaChapter payload={payload} headingRef={headingRef} onNext={next} />}
        {chapter === 3 && <StrengthsChapter payload={payload} headingRef={headingRef} onNext={next} />}
        {chapter === 4 && <GrowthChapter payload={payload} headingRef={headingRef} onNext={next} />}
        {chapter === 5 && <MatchesChapter payload={payload} headingRef={headingRef} onNext={next} />}
        {chapter === 6 && (
          <NextStepChapter
            payload={payload}
            headingRef={headingRef}
            onAction={() => navigate("/command-center")}
          />
        )}
      </div>
      )}

      {chapter > 0 && (
        <div className="mt-10 flex justify-center print:hidden">
          <button
            onClick={back}
            className="inline-flex items-center gap-1 text-xs text-muted-foreground/60 hover:text-muted-foreground transition-colors"
          >
            <ArrowLeft className="h-3 w-3" aria-hidden="true" /> Previous chapter
          </button>
        </div>
      )}
    </main>
  );
}

/* ------------------------------------------------------------------ */
/* Shared chapter scaffolding                                          */
/* ------------------------------------------------------------------ */

type HeadingRef = React.RefObject<HTMLHeadingElement>;

function ChapterHeading({ headingRef, kicker, children }: {
  headingRef: HeadingRef; kicker?: string; children: React.ReactNode;
}) {
  return (
    <header className="text-center mb-10">
      {kicker && (
        <p className="mb-3 text-[11px] uppercase tracking-[0.2em] text-primary/70">{kicker}</p>
      )}
      <h2
        ref={headingRef}
        tabIndex={-1}
        className="font-display text-3xl sm:text-4xl font-bold outline-none text-balance"
      >
        {children}
      </h2>
    </header>
  );
}

function ChapterAction({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <div className="mt-10 flex justify-center print:hidden">
      <Button size="lg" onClick={onClick} className="gap-2 rounded-full px-8">
        {label} <ArrowRight className="h-4 w-4" aria-hidden="true" />
      </Button>
    </div>
  );
}

function EmptyBlock({ label }: { label: string }) {
  return (
    <div className="rounded-xl border border-dashed border-border/60 p-4 text-xs text-muted-foreground text-center">
      {label}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* 1 · The Letter                                                      */
/* ------------------------------------------------------------------ */

function LetterChapter({ name, headingRef, onNext }: {
  name: string | null; headingRef: HeadingRef; onNext: () => void;
}) {
  return (
    <section aria-label="A letter for you">
      <ChapterHeading headingRef={headingRef}>
        Hello{name ? ` ${name}` : ""},
      </ChapterHeading>
      <div className="mx-auto max-w-prose space-y-5 text-base sm:text-lg leading-relaxed text-foreground/90">
        <p>Thank you for trusting us with your story.</p>
        <p>
          Over the last hour you shared far more than answers. You shared
          patterns. Strengths. Curiosity. Dreams.
        </p>
        <p>
          This report isn't here to tell you who you should become. It's here to
          help you better understand who you already are.
        </p>
        <p className="text-primary font-medium">Let's discover your story together.</p>
      </div>
      <ChapterAction label="Discover your story" onClick={onNext} />
    </section>
  );
}

/* ------------------------------------------------------------------ */
/* 2 · Your Story™ — narrative before any number                       */
/* ------------------------------------------------------------------ */

function StoryChapter({ payload, headingRef, onNext }: {
  payload: StudentPayload; headingRef: HeadingRef; onNext: () => void;
}) {
  const top = payload.career_dna.top_intelligences;
  return (
    <section aria-label="Your Story">
      <ChapterHeading headingRef={headingRef} kicker="Chapter one">Your Story™</ChapterHeading>
      <div className="mx-auto max-w-prose space-y-5 leading-relaxed text-foreground/90">
        <p>
          While you answered, a picture began to emerge — not about your career.
          About you. Here is what stood out most clearly:
        </p>
        {top.length === 0 && <EmptyBlock label="Your story is still being written — check back soon." />}
        {top.map((r) => (
          <StoryParagraph key={r.intelligence_code} row={r} />
        ))}
        <p className="text-muted-foreground text-sm">
          None of this is a verdict. It's a snapshot of how your strengths
          express themselves right now — and strengths grow.
        </p>
      </div>
      <ChapterAction label="Meet your Career DNA™" onClick={onNext} />
    </section>
  );
}

function StoryParagraph({ row }: { row: IntelRow }) {
  // Narrative chapter: the engine's canonical sentence, no numbers.
  // Editorial treatment — a quiet accent rule instead of a card.
  return (
    <div className="border-l-2 border-primary/40 pl-5 py-1">
      <p className="font-display text-lg font-semibold text-primary">{row.intelligence_name}</p>
      <p className="mt-1.5 text-base leading-relaxed text-foreground/85">
        {row.explainability?.explanation ?? "A clear signal in how you responded."}
      </p>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* 3 · Career DNA™ — meaning first, numbers second                     */
/* ------------------------------------------------------------------ */

/**
 * Career DNA™ presentation — top intelligences with the engine's own
 * explanations, verbatim. One component, two skins:
 *
 *  - `variant="card"` (default) — compact tiles for the Future Command
 *    Center™, where a dashboard grammar is the right grammar.
 *  - `variant="editorial"` — the Living Report™ reading experience: no
 *    boxes, generous type, meaning before magnitude. The number arrives
 *    after the sentence that explains it (V-EXP-05 in miniature).
 *
 * Identical data either way; only the markup differs.
 */
export function CareerDnaCards({ top, variant = "card" }: {
  top: IntelRow[]; variant?: "card" | "editorial";
}) {
  if (top.length === 0) return <EmptyBlock label="No intelligences available yet" />;

  if (variant === "editorial") {
    return (
      <div className="mx-auto max-w-prose divide-y divide-border/40">
        {top.map((r) => (
          <article key={r.intelligence_code} className="py-7 first:pt-0 last:pb-0">
            <h3 className="font-display text-xl sm:text-2xl font-semibold text-primary">
              {r.intelligence_name}
            </h3>
            {r.explainability && (
              <p className="mt-2 text-base leading-relaxed text-foreground/85">
                {r.explainability.explanation}
              </p>
            )}
            <SignalLine label={r.intelligence_name} value={r.value} />
          </article>
        ))}
      </div>
    );
  }

  return (
    <div className="grid gap-3 sm:grid-cols-3">
      {top.map((r) => (
        <Card key={r.intelligence_code} className="border-primary/30">
          <CardContent className="p-4">
            <div className="font-display font-semibold text-sm">{r.intelligence_name}</div>
            <div className="mt-1 font-display text-2xl font-bold text-primary">{pct(r.value)}</div>
            <Progress value={bar(r.value)} className="mt-2 h-1.5" aria-label={`${r.intelligence_name} signal`} />
            {r.explainability && (
              <p className="mt-2 text-xs text-muted-foreground">{r.explainability.explanation}</p>
            )}
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

/**
 * A signal's magnitude, rendered as a quiet hairline rather than a
 * dashboard progress bar — supporting evidence, not a headline.
 */
function SignalLine({ label, value }: { label: string; value: number | null }) {
  return (
    <div className="mt-4 flex items-center gap-3">
      <div
        className="h-px flex-1 bg-border/60"
        role="img"
        aria-label={`${label}: ${pct(value)} of 100`}
      >
        <div className="h-px bg-primary/70" style={{ width: `${bar(value)}%` }} />
      </div>
      <span className="text-xs tabular-nums text-muted-foreground shrink-0">{pct(value)}</span>
    </div>
  );
}

function DnaChapter({ payload, headingRef, onNext }: {
  payload: StudentPayload; headingRef: HeadingRef; onNext: () => void;
}) {
  const [showAll, setShowAll] = useState(false);
  const top = payload.career_dna.top_intelligences;
  return (
    <section aria-label="Career DNA">
      <ChapterHeading headingRef={headingRef} kicker="Chapter two">Career DNA™</ChapterHeading>
      <div className="mx-auto max-w-prose space-y-5 leading-relaxed text-foreground/90">
        <p>
          Your Career DNA™ is a picture of how your human intelligences express
          themselves right now. It is not a grade, and it is not fixed — it
          grows as you do. A higher signal simply means "more visible today."
        </p>
      </div>
      <div className="mt-10">
        <CareerDnaCards top={top} variant="editorial" />
      </div>
      {payload.hdi_summary.length > 0 && (
        <Disclosure
          open={showAll}
          onToggle={() => setShowAll((s) => !s)}
          openLabel="See your full picture"
          closeLabel="Show fewer"
        >
          <div className="mx-auto max-w-prose divide-y divide-border/40 text-left">
            {payload.hdi_summary.map((r) => (
              <SignalRow
                key={r.intelligence_code}
                label={r.intelligence_name}
                value={r.value}
                explanation={r.explainability?.explanation}
              />
            ))}
          </div>
        </Disclosure>
      )}
      <ChapterAction label="See what comes naturally to you" onClick={onNext} />
    </section>
  );
}

function SignalRow({ label, value, explanation }: {
  label: string; value: number | null; explanation?: string;
}) {
  return (
    <div className="py-5 first:pt-0 last:pb-0">
      <h4 className="font-display text-base font-semibold">{label}</h4>
      {explanation && (
        <p className="mt-1.5 text-sm leading-relaxed text-foreground/75">{explanation}</p>
      )}
      <SignalLine label={label} value={value} />
    </div>
  );
}

/**
 * Progressive disclosure that reads as turning a page rather than
 * expanding a widget — a centred hairline rule with the control set into it.
 */
function Disclosure({ open, onToggle, openLabel, closeLabel, children }: {
  open: boolean; onToggle: () => void;
  openLabel: string; closeLabel: string; children: React.ReactNode;
}) {
  return (
    <div className="mt-10">
      <div className="flex items-center gap-4">
        <div className="h-px flex-1 bg-border/50" aria-hidden="true" />
        <button
          onClick={onToggle}
          aria-expanded={open}
          className="text-xs uppercase tracking-[0.15em] text-muted-foreground hover:text-primary transition-colors"
        >
          {open ? closeLabel : openLabel}
        </button>
        <div className="h-px flex-1 bg-border/50" aria-hidden="true" />
      </div>
      {open && <div className="mt-8 animate-fade-in">{children}</div>}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* 4 · Your Strengths™ — opportunities, not labels                     */
/* ------------------------------------------------------------------ */

function StrengthsChapter({ payload, headingRef, onNext }: {
  payload: StudentPayload; headingRef: HeadingRef; onNext: () => void;
}) {
  const skills = payload.future_skills;
  return (
    <section aria-label="Your Strengths">
      <ChapterHeading headingRef={headingRef} kicker="Chapter three">Your Strengths™</ChapterHeading>
      <div className="mx-auto max-w-prose space-y-5 leading-relaxed text-foreground/90">
        <p>
          A strength isn't a label — it's a door. These are the ones already
          open to you, including the skills the engine flags as pointed at the
          future.
        </p>
      </div>
      <div className="mt-10">
        {skills.length > 0 ? (
          <div className="mx-auto max-w-prose divide-y divide-border/40">
            {skills.map((f) => (
              <StrengthCard key={f.feature_key} row={f} />
            ))}
          </div>
        ) : (
          <EmptyBlock label="Your strengths picture is still forming — your Career DNA™ already shows the strongest signals." />
        )}
      </div>
      <ChapterAction label="See where you can grow" onClick={onNext} />
    </section>
  );
}

function StrengthCard({ row }: { row: FeatureRow }) {
  return (
    <article className="py-7 first:pt-0 last:pb-0">
      <h3 className="font-display text-lg sm:text-xl font-semibold">{row.feature_label}</h3>
      {row.explainability?.explanation && (
        <p className="mt-2 text-base leading-relaxed text-foreground/85">
          {row.explainability.explanation}
        </p>
      )}
      <SignalLine label={row.feature_label} value={row.value} />
    </article>
  );
}

/* ------------------------------------------------------------------ */
/* 5 · Your Growth Opportunities™ — invitations, never weaknesses      */
/* ------------------------------------------------------------------ */

function GrowthChapter({ payload, headingRef, onNext }: {
  payload: StudentPayload; headingRef: HeadingRef; onNext: () => void;
}) {
  return (
    <section aria-label="Your Growth Opportunities">
      <ChapterHeading headingRef={headingRef} kicker="Chapter four">
        Your Growth Opportunities™
      </ChapterHeading>
      <div className="mx-auto max-w-prose space-y-5 leading-relaxed text-foreground/90">
        <p>
          These aren't weaknesses. They're invitations — the places where a
          little attention grows the fastest. Every one of them is learnable.
        </p>
      </div>
      <div className="mt-10">
        {payload.growth_opportunities.length > 0 ? (
          <div className="mx-auto max-w-prose divide-y divide-border/40">
            {payload.growth_opportunities.map((f) => (
              <StrengthCard key={f.feature_key} row={f} />
            ))}
          </div>
        ) : (
          <EmptyBlock label="No growth opportunities identified yet" />
        )}
      </div>
      <ChapterAction label="See your future career matches" onClick={onNext} />
    </section>
  );
}

/* ------------------------------------------------------------------ */
/* 6 · Future Career Matches™ — after self-understanding, with WHY     */
/* ------------------------------------------------------------------ */

/**
 * One recommendation with its canonical WHY (engine explanation verbatim,
 * V-EXP-02 discipline). Two skins, same data — see `CareerDnaCards`.
 */
export function RecommendationWhyCard({ rec: r, variant = "card" }: {
  rec: RecRow; variant?: "card" | "editorial";
}) {
  const why = r.explainability?.explanation ?? r.explanation;

  if (variant === "editorial") {
    return (
      <article className="py-7 first:pt-0 last:pb-0">
        <div className="flex items-baseline justify-between gap-4">
          <Link
            to={`/careers/${encodeURIComponent(r.career_id)}`}
            className="font-display text-xl sm:text-2xl font-semibold hover:text-primary transition-colors"
          >
            {r.career_name}
          </Link>
          <span className="text-xs tabular-nums text-muted-foreground shrink-0">
            {pct(r.recommendation_score)}
          </span>
        </div>
        <p className="mt-0.5 text-sm text-muted-foreground">{r.career_cluster}</p>
        {why && (
          <p className="mt-3 text-base leading-relaxed text-foreground/85">
            <span className="text-primary font-medium">Why this one — </span>
            {why}
          </p>
        )}
      </article>
    );
  }

  return (
    <Card className="border-border/50">
      <CardContent className="p-4">
        <div className="flex items-baseline justify-between gap-3">
          <div>
            <Link
              to={`/careers/${encodeURIComponent(r.career_id)}`}
              className="font-display font-semibold hover:text-primary transition-colors"
            >
              {r.career_name}
            </Link>
            <div className="text-[11px] uppercase tracking-wider text-muted-foreground">
              {r.career_cluster}
            </div>
          </div>
          <span className="text-xs font-mono text-muted-foreground">{pct(r.recommendation_score)}</span>
        </div>
        {why && (
          <p className="mt-2 text-sm text-muted-foreground">
            <span className="text-primary font-medium">Why: </span>
            {why}
          </p>
        )}
      </CardContent>
    </Card>
  );
}

function MatchesChapter({ payload, headingRef, onNext }: {
  payload: StudentPayload; headingRef: HeadingRef; onNext: () => void;
}) {
  const [showAll, setShowAll] = useState(false);
  const recs = payload.recommendations;
  return (
    <section aria-label="Future Career Matches">
      <ChapterHeading headingRef={headingRef} kicker="Chapter five">
        Future Career Matches™
      </ChapterHeading>
      <div className="mx-auto max-w-prose space-y-5 leading-relaxed text-foreground/90">
        <p>
          Now that you've met yourself, meet some futures. Each match comes with
          its why — you should never have to wonder where a recommendation came
          from.
        </p>
      </div>
      <div className="mt-10">
        {recs.length > 0 ? (
          <div className="mx-auto max-w-prose divide-y divide-border/40">
            {recs.map((r) => (
              <RecommendationWhyCard key={r.career_id} rec={r} variant="editorial" />
            ))}
          </div>
        ) : (
          <EmptyBlock label="No recommendations yet" />
        )}
      </div>
      {payload.career_matches.length > 0 && (
        <Disclosure
          open={showAll}
          onToggle={() => setShowAll((s) => !s)}
          openLabel={`Explore all ${payload.career_matches.length} matches`}
          closeLabel="Show fewer"
        >
          <div className="mx-auto max-w-prose divide-y divide-border/40 text-left">
            {payload.career_matches.map((c) => (
              <article key={c.career_id} className="py-5 first:pt-0 last:pb-0">
                <div className="flex items-baseline justify-between gap-4">
                  <Link
                    to={`/careers/${encodeURIComponent(c.career_id)}`}
                    className="font-display text-base font-semibold hover:text-primary transition-colors"
                  >
                    {c.career_name}
                  </Link>
                  <span className="text-xs tabular-nums text-muted-foreground shrink-0">
                    {pct(c.fit_score)}
                  </span>
                </div>
                <p className="mt-0.5 text-xs text-muted-foreground">{c.career_cluster}</p>
                {c.explainability && (
                  <p className="mt-2 text-sm leading-relaxed text-foreground/75">
                    {c.explainability.explanation}
                  </p>
                )}
              </article>
            ))}
          </div>
        </Disclosure>
      )}
      <ChapterAction label="Take your next step" onClick={onNext} />
    </section>
  );
}

/* ------------------------------------------------------------------ */
/* 7 · One Next Step™ — exactly one action (V-EXP-03)                  */
/* ------------------------------------------------------------------ */

function NextStepChapter({ payload, headingRef, onAction }: {
  payload: StudentPayload; headingRef: HeadingRef; onAction: () => void;
}) {
  return (
    <section aria-label="One Next Step" className="text-center">
      <Sparkles className="mx-auto mb-4 h-8 w-8 text-primary" aria-hidden="true" />
      <ChapterHeading headingRef={headingRef}>One Next Step™</ChapterHeading>
      <div className="mx-auto max-w-prose space-y-5 leading-relaxed text-foreground/90">
        <p>
          You've met your story, your strengths, and some possible futures.
          Understanding yourself was the hard part — and you've done it.
        </p>
        <p className="font-medium">Now let's turn it into a future you can see.</p>
      </div>
      <div className="mt-10 flex justify-center">
        <Button size="lg" onClick={onAction} className="gap-2 rounded-full px-10 py-6 text-base">
          Enter your Future Command Center™ <ArrowRight className="h-5 w-5" aria-hidden="true" />
        </Button>
      </div>

      {/* Trust, quietly: full provenance available, never competing. */}
      <details className="mt-14 mx-auto max-w-prose text-left">
        <summary className="cursor-pointer text-xs text-muted-foreground/60 hover:text-muted-foreground text-center list-none">
          How this report was made
        </summary>
        <div className="mt-3 rounded-xl border border-border/40 bg-card/30 p-4 text-[11px] font-mono text-muted-foreground space-y-1 break-words">
          <div>engine_version: {payload.provenance.engine_version}</div>
          <div>item_bank_version: {payload.provenance.item_bank_version}</div>
          <div>feature_registry_version: {payload.provenance.feature_registry_version}</div>
          <div>intelligence_registry_version: {payload.provenance.intelligence_registry_version}</div>
          <div>career_dataset_version: {payload.provenance.career_dataset_version}</div>
          <div>recommendation_recipe_version: {payload.provenance.recommendation_recipe_version}</div>
          <div>experience_version: {payload.provenance.experience_version}</div>
          <div>experience_template_id: {payload.provenance.experience_template_id}</div>
          <div>generated_at: {payload.provenance.generated_at}</div>
          <div className="pt-2 border-t border-border/40">
            <div className="mb-1">computation_hashes</div>
            {Object.entries(payload.provenance.computation_hashes).map(([k, v]) => (
              <div key={k}>{k}: {v ?? "—"}</div>
            ))}
          </div>
          <div className="pt-2 border-t border-border/40">report_hash: {payload.report_hash}</div>
        </div>
      </details>

      {/* Licence condition: careers and occupation descriptors in this report
          draw on O*NET Web Services. */}
      <OnetAttribution variant="card" className="mt-8 mx-auto max-w-prose text-left" />
    </section>
  );
}
